<template>
  <div class="kdp">
    <!-- INPUT -->
    <div class="kdp-input" @click="toggle">
      <div class="kdp-input__text">{{ displayValue }}</div>
      <div class="kdp-input__icon" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm12 8H5v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9ZM6 6a1 1 0 0 0-1 1v1h14V7a1 1 0 0 0-1-1H6Z"
          />
        </svg>
      </div>
    </div>

    <!-- POPOVER -->
    <div v-if="open" class="kdp-popover" ref="popover" :style="popoverStyle">
      <div class="kdp-panel">
        <!-- HEADER -->
        <div class="kdp-header">
          <button class="kdp-icbtn" type="button" @click="decYear"><font-awesome-icon icon="fa-solid fa-chevron-down" class="fa" /></button>
          <button class="kdp-icbtn" type="button" @click="prevMonth"><font-awesome-icon icon="fa-solid fa-chevron-left" class="fa" /></button>

          <div class="kdp-title">{{ monthTitle }}</div>

          <button class="kdp-icbtn" type="button" @click="nextMonth"><font-awesome-icon icon="fa-solid fa-chevron-right" class="fa" /></button>
          <button class="kdp-icbtn" type="button" @click="incYear"><font-awesome-icon icon="fa-solid fa-chevron-up" class="fa" /></button>
        </div>

        <!-- WEEKDAYS -->
        <div class="kdp-weekdays">
          <div v-for="h in headers" :key="h" class="kdp-weekday">
            {{ h }}
          </div>
        </div>

        <!-- DAYS GRID -->
        <div class="kdp-days">
          <button
            v-for="(cell, idx) in cells"
            :key="cellKey(cell, idx)"
            type="button"
            class="kdp-day"
            :class="dayClass(cell)"
            :disabled="!extractDayNumber(cell)"
            @click="pick(cell)"
          >
            {{ cellLabel(cell) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toEthiopian } from 'ethiopian-calendar-new';
import { MonthGrid } from "kenat";
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronUp, faChevronDown, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { MONTHS_ETHIOPIAN } from '@/utils/const';

library.add(faChevronUp, faChevronDown, faChevronLeft, faChevronRight);

export default {
  name: "KenatEtDatePicker",

  props: {
    modelValue: { type: Object, default: null }, // {year,month,day} | null (Vue 3 v-model)
    placeholder: { type: String, default: "MM-DD-YYYY" },

    // Kenat options (since your MonthGrid supports these keys)
    weekStart: { type: Number, default: 0 }, // depends on kenat, keep as string
    useGeez: { type: Boolean, default: false },
    weekdayLang: { type: String, default: "amharic" }, // or "english"
    mode: { type: String, default: "month" },     // depends on kenat
    showAllSaints: { type: Boolean, default: false },

    initialYear: { type: Number, default: 2017 },
    initialMonth: { type: Number, default: 1 }
  },

  data() {
    const now = new Date();
    const currentEth = toEthiopian(now.getFullYear(), now.getMonth() + 1, now.getDate());
    return {
      open: false,
      viewYear: (this.modelValue && this.modelValue.year) || currentEth.year,
      viewMonth: (this.modelValue && this.modelValue.month) || currentEth.month,
      popoverStyle: {}
    };
  },

  computed: {
    displayValue() {
      const val = this.modelValue;
      if (!val || !val.year || !val.month || !val.day) return this.placeholder;
      return `${MONTHS_ETHIOPIAN[val.month - 1]} ${this.pad(val.day, 2)}, ${this.pad(val.year, 4)}`;
    },

    // Build MonthGrid instance
    mg() {
      return new MonthGrid({
        year: this.viewYear,
        month: this.viewMonth,
        weekStart: this.weekStart,
        useGeez: this.useGeez,
        weekdayLang: this.weekdayLang,
        mode: this.mode,
        showAllSaints: this.showAllSaints
      });
    },

    // IMPORTANT: call generate()
    generated() {
      // generate() returns the object containing headers + days merged
      return this.mg.generate();
    },

    headers() {
      // generate() should include weekday headers
      return (this.generated && this.generated.headers) || [];
    },

    cells() {
      // days could be 2D (weeks). Flatten.
      const d = (this.generated && this.generated.days) || [];
      return Array.isArray(d[0]) ? d.flat() : d;
    },

    monthTitle() {
      // Kenat can localize month/year internally, but easiest:
      // generate() often returns title/monthName/yearText — try them:
      const g = this.generated || {};
      return (
        g.title ||
        (g.monthName && g.yearText ? `${g.monthName} ${g.yearText}` : null) ||
        (g.monthName ? `${g.monthName} ${this.viewYear}` : null) ||
        `${this.viewYear} / ${this.viewMonth}`
      );
    }
  },

  watch: {
    modelValue(v) {
      if (v && v.year && v.month) {
        this.viewYear = v.year;
        this.viewMonth = v.month;
      }
    }
  },

  mounted() {
    document.addEventListener("mousedown", this.onOutside);
    document.addEventListener("touchstart", this.onOutside);
  },

  beforeDestroy() {
    document.removeEventListener("mousedown", this.onOutside);
    document.removeEventListener("touchstart", this.onOutside);
  },

  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        if (!this.modelValue) {
          const now = new Date();
          const eth = toEthiopian(now.getFullYear(), now.getMonth() + 1, now.getDate());
          this.viewYear = eth.year;
          this.viewMonth = eth.month;
        }
        this.$nextTick(() => {
          const input = this.$el.querySelector('.kdp-input');
          if (input) {
            const rect = input.getBoundingClientRect();
            this.popoverStyle = {
              position: 'fixed',
              top: `${rect.bottom + 4}px`,
              left: `${rect.left}px`,
            };
          }
        });
      }
    },
    close() { this.open = false; },

    onOutside(e) {
      if (!this.open) return;
      if (!this.$el.contains(e.target)) this.close();
    },

    pad(n, len) {
      const s = String(n);
      return s.length >= len ? s : "0".repeat(len - s.length) + s;
    },

    // Kenat navigation: use up()/down() then sync year/month from object
    prevMonth() {
      const g = this.mg.down(); // should move back
      this.viewYear = g.year;
      this.viewMonth = g.month;
    },

    nextMonth() {
      const g = this.mg.up(); // should move forward
      this.viewYear = g.year;
      this.viewMonth = g.month;
    },

    incYear() { this.viewYear += 1; },
    decYear() { this.viewYear -= 1; },

    cellKey(cell, idx) {
      const et = this.extractEthiopianDate(cell);
      if (!et) return `blank-${idx}`;
      return `${et.year}-${et.month}-${et.day}`;
    },

    dayClass(cell) {
      const et = this.extractEthiopianDate(cell);
      if (!et) return ["is-blank"];

      const isSelected =
        this.modelValue &&
        this.modelValue.year === et.year &&
        this.modelValue.month === et.month &&
        this.modelValue.day === et.day;

      // out-of-month flag can vary by Kenat version
      const isOut =
        cell && (cell.isCurrentMonth === false || cell.outOfMonth === true);

      return {
        "is-selected": !!isSelected,
        "is-today": !!(cell && cell.isToday),
        "is-out": !!isOut
      };
    },

    pick(cell) {
      const et = this.extractEthiopianDate(cell);
      if (!et) return;

      this.$emit("update:modelValue", et);
      this.$emit("input", et);
      this.$emit("change", et);
      this.close();
    },

    extractDayNumber(cell) {
      if (cell == null) return null;

      // Cell could be a number (1..30)
      if (typeof cell === "number") return cell;

      // Or string "1"
      if (typeof cell === "string" && /^\d+$/.test(cell)) return parseInt(cell, 10);

      // Most common nested shapes
      return (
        cell.day ??
        cell.d ??
        cell.dayNumber ??
        (cell.ethiopian && cell.ethiopian.day) ??
        (cell.et && cell.et.day) ??
        (cell.ec && cell.ec.day) ??
        (cell.date && cell.date.day) ??
        (cell.value && cell.value.day) ??
        null
      );
    },

    extractEthiopianDate(cell) {
      if (cell == null) return null;

      // If already a full ET date object
      const et =
        cell.ethiopian ||
        cell.et ||
        cell.ec ||
        cell.ethiopic ||
        cell.ethiopianDate ||
        (cell.date && (cell.date.ethiopian || cell.date.et || cell.date.ec)) ||
        null;

      const day = this.extractDayNumber(cell);

      // If Kenat cell doesn't carry year/month, use current view
      const year = (et && et.year) || cell.year || this.viewYear;
      const month = (et && et.month) || cell.month || this.viewMonth;

      if (month !== this.viewMonth) return null;

      if (!day) return null;

      return { year, month, day };
    },

    cellLabel(cell) {
      const d = this.extractDayNumber(cell);
      return d ? String(d) : "";
    },
  }
};
</script>

<style scoped>
/* Scale factor: 0.75 — everything proportionally smaller */
.kdp { position: relative; font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; }

.kdp-input{
  height: 39px;
  border-radius: 9px;
  background: #fff;
  display:flex; 
  align-items:center; 
  justify-content:space-between;
  padding: 0 10px;
  cursor:pointer;
  user-select:none;
  font-size: 0.875rem !important;
  line-height: 1rem;
  border: 1px solid #e6e6e8 !important;
  border-radius: 6px !important;
}

.kdp-popover{ position:absolute; top: 46px; left:0; z-index: 1070; }

.kdp-panel{
  width: 315px;
  border-radius: 13px;
  border: 1px solid rgba(0,0,0,0.14);
  background: #fff;
  padding: 10px 10px 12px;
  box-shadow: 0 14px 30px rgba(0,0,0,0.18);
  color:#111;
}

.kdp-header{
  display:grid;
  grid-template-columns: 27px 27px 1fr 27px 27px;
  align-items:center;
  gap: 7px;
  margin-bottom: 7px;
}
.kdp-icbtn{
  height: 26px; width: 26px;
  border-radius: 7px;
  border: 1px solid rgba(0,0,0,0.16);
  background: #fff;
  color:#111;
  cursor:pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kdp-icbtn .fa { font-size: 10px; }
.kdp-icbtn:hover{ background: rgba(0,0,0,0.04); }
.kdp-title{ text-align:center; font-weight:600; font-size:12px; }

.kdp-weekdays{
  display:grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 7px;
  padding: 3px 2px 7px;
  color: rgba(0,0,0,0.65);
}
.kdp-weekday{ text-align:center; font-size: 9px; }

.kdp-days{
  display:grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 7px;
}
.kdp-day{
  height: 39px;
  border-radius: 9px;
  border: 1px solid rgba(0,0,0,0.22);
  background: rgba(0,0,0,0.0075);
  color:#111;
  font-size: 12px;
  cursor:pointer;
}
.kdp-day:hover{ border-color: #2E7918; }

.kdp-day.is-blank{
  border-color: transparent !important;
  background: transparent !important;
  cursor: default;
  pointer-events: none;
}

.kdp-day.is-out{ opacity: 0.35; }

.kdp-day.is-selected{
  background: #2E7918;
  border-color: #2E7918;
  color: #fff;
}

.kdp-day.is-today{
  border: 2px solid #2E7918;
}

.kdp-weekdays,
.kdp-days {
  direction: ltr;
}

/* Optional: keep header buttons layout the same */
.kdp-header {
  direction: ltr;
}
</style>